// eslint-disable-next-line no-restricted-imports -- Required for Node.js script compatibility
import { BULLET_VARIANTS, CONTENT_BLOCK_TYPES, FAQ_NOTE_ACTIONS } from './faq.const.js'
// eslint-disable-next-line no-restricted-imports -- Required for Node.js script compatibility
import { ROUTES } from '../../router/const.js'
// eslint-disable-next-line no-restricted-imports -- Required for Node.js script compatibility
import { TICKET_EDIT_STEPS } from './ticket-creation.const.js'

/** Help Center article slug - URL segment, FAQ_ARTICLES key, and `[[label|…]]` link target (single source of truth). */
export const FAQ_ARTICLE_IDS = {
  WHAT_IS_THIS_PLATFORM: 'what-is-this-platform',
  KEY_CONCEPTS: 'key-concepts',
  YOUR_HOME_DASHBOARD: 'your-home-dashboard',
  WHAT_IS_A_REQUEST: 'what-is-a-request',
  HOW_TO_SUBMIT: 'how-to-submit',
  WHAT_IS_A_DRAFT: 'what-is-a-draft',
  UPLOADING_FILES: 'uploading-files',
  TRACKING_YOUR_REQUEST: 'tracking-your-request',
  REQUEST_APPROVED: 'request-approved',
  REQUEST_DENIED: 'request-denied',
  DUPLICATING_A_REQUEST: 'duplicating-a-request',
  WHAT_IS_AN_APPROVAL: 'what-is-an-approval',
  ACTIVE_VS_REVOKED: 'active-vs-revoked',
  HOW_TO_REVOKE: 'how-to-revoke',
  WHAT_ARE_CONFIRMATIONS: 'what-are-confirmations',
  HOW_TO_CONFIRM: 'how-to-confirm',
  CONTACTING_SUPPORT: 'contacting-support',
  PRODUCT_NOT_IN_CATALOG: 'product-not-in-catalog',
  INTEGRATIONS: 'integrations',
  ADDITIONAL_EMAIL_ADDRESSES: 'additional-email-addresses',
  SET_UP_NOTIFICATIONS: 'set-up-notifications',
  NOTIFICATIONS_NOT_ARRIVING: 'notifications-not-arriving',
}

// ---------------------------------------------------------------------------
// Quick links - 8 most-searched articles, shown on the landing page
// ---------------------------------------------------------------------------
export const FAQ_QUICK_LINKS = [
  { label: 'How do I submit a request?', articleId: FAQ_ARTICLE_IDS.HOW_TO_SUBMIT },
  { label: 'How do I track my request status?', articleId: FAQ_ARTICLE_IDS.TRACKING_YOUR_REQUEST },
  {
    label: 'What happens when my request is approved?',
    articleId: FAQ_ARTICLE_IDS.REQUEST_APPROVED,
  },
  {
    label: 'What is a confirmation and why is it required?',
    articleId: FAQ_ARTICLE_IDS.WHAT_ARE_CONFIRMATIONS,
  },
  { label: 'How do I confirm I am still using a tool?', articleId: FAQ_ARTICLE_IDS.HOW_TO_CONFIRM },
  {
    label: 'What is the difference between a request and an approval?',
    articleId: FAQ_ARTICLE_IDS.KEY_CONCEPTS,
  },
  { label: 'My request was denied - what can I do?', articleId: FAQ_ARTICLE_IDS.REQUEST_DENIED },
  {
    label: 'The product I need is not in the catalog',
    articleId: FAQ_ARTICLE_IDS.PRODUCT_NOT_IN_CATALOG,
  },
]

// Flat map of all articles, keyed by article id (used as URL param).
export const FAQ_ARTICLES = {
  // ── Getting Started ────────────────────────────────────────────────────
  [FAQ_ARTICLE_IDS.WHAT_IS_THIS_PLATFORM]: {
    title: 'What is this platform?',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: "AI is changing the way we work - but with that comes the need to use AI products responsibly. Your organization uses this platform to make sure every AI product in use is properly reviewed and approved.\nWhen you need an AI product for your work, this is where you request access - and where you manage that access once it's granted.",
      },
      {
        id: 'walkthrough-note',
        type: CONTENT_BLOCK_TYPES.NOTE,
        icon: 'compass',
        label: 'See it for yourself',
        text: 'Follow one request from draft to decision. The tour runs on example data, so you can click everything - your own requests stay put.',
        action: FAQ_NOTE_ACTIONS.REPLAY_WALKTHROUGH,
      },
      {
        id: 'what-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'What you can do',
        icon: 'globe',
        color: 'brand',
        blocks: [
          {
            id: 'what-journey',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.JOURNEY,
            items: [
              {
                id: 'journey-request',
                icon: 'clipboard-clock',
                title: 'Request access',
                text: `[[Submit a request|${FAQ_ARTICLE_IDS.HOW_TO_SUBMIT}]] for an AI product you need. A guided form walks you through the process - you can even save a draft and come back later.`,
              },
              {
                id: 'journey-approval',
                icon: 'badge-check',
                title: 'Manage your approval',
                text: `Once approved, a separate [[approval|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]] tracks your access going forward. You can view it anytime under **Product approvals**.`,
              },
              {
                id: 'journey-confirm',
                icon: 'clock-check',
                title: 'Confirm periodically',
                text: `Your organization may ask you to [[confirm|${FAQ_ARTICLE_IDS.WHAT_ARE_CONFIRMATIONS}]] you're still using the product. A quick check-in keeps your access in good standing.`,
              },
              {
                id: 'journey-dashboard',
                icon: 'layout-panel-top',
                title: 'See it all at a glance',
                text: `Your [[home dashboard|${FAQ_ARTICLE_IDS.YOUR_HOME_DASHBOARD}]] ties it together - open requests, upcoming confirmations, and recent activity in one place.`,
              },
            ],
          },
        ],
      },
      {
        id: 'why-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Why does my organization use this?',
        icon: 'building',
        color: 'info',
        blocks: [
          {
            id: 'why-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: 'AI products are powerful, but using them without oversight can create security, privacy, and compliance risks - for you and your organization.\nThis platform makes sure every AI product in use has gone through a **transparent approval process** with an **auditable record**. That protects everyone involved while still making it easy for you to get the products you need.',
          },
        ],
      },
      {
        id: 'who-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Who can see my requests and approvals?',
        icon: 'users',
        color: 'positive',
        blocks: [
          {
            id: 'who-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: "Only **you** and the **administrators** who manage AI access for your organization. Other employees can't see your requests, approvals, or any information you've submitted.",
          },
        ],
      },
    ],
    faqs: [
      {
        id: 'how-to-get-started',
        question: 'How do I get started?',
        answer: `Click {btn:request-approval} in the left menu to submit your first request. A guided form will walk you through selecting the product and answering your organization's questions. See [[how to submit a request|${FAQ_ARTICLE_IDS.HOW_TO_SUBMIT}]] for a full walkthrough.`,
      },
      {
        id: 'is-it-mandatory',
        question: 'Do I have to use this platform to access AI products?',
        answer:
          "Yes - your organization requires all AI product access to go through this platform. Using AI products outside the governance process may violate your organization's policies.\nThe good news is that submitting a request only takes a few minutes, and you can save your progress as a draft if you need to come back later.",
      },
    ],
  },

  [FAQ_ARTICLE_IDS.KEY_CONCEPTS]: {
    title: 'Key concepts',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: 'AI products are becoming part of everyday work - but they come with real risks around data privacy, security, and compliance.\nAI governance is how your organization manages that. This platform is where the process lives for you.',
      },
      {
        id: 'governance-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Why AI governance matters',
        icon: 'shield-check',
        color: 'info',
        blocks: [
          {
            id: 'governance-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: 'When employees adopt AI products independently, organizations lose visibility into what data is being shared and whether usage meets regulatory requirements.\nThis platform creates a **transparent approval process** with an **auditable record** - so you get a clear path to the products you need without creating compliance issues.',
          },
        ],
      },
      {
        id: 'entities-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Request, approval, confirmation',
        icon: 'workflow',
        color: 'brand',
        blocks: [
          {
            id: 'entities-intro',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: 'Three things, each with its own lifecycle:',
          },
          {
            id: 'entities-journey',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.JOURNEY,
            items: [
              {
                id: 'entity-request',
                icon: 'clipboard-clock',
                title: 'Request',
                text: `How you [[ask for access|${FAQ_ARTICLE_IDS.HOW_TO_SUBMIT}]] to an AI product. An administrator reviews it and makes a decision - then the request closes. See [[what is a request|${FAQ_ARTICLE_IDS.WHAT_IS_A_REQUEST}]].`,
              },
              {
                id: 'entity-approval',
                icon: 'badge-check',
                title: 'Approval',
                text: `Created when your request is accepted. It's **separate** from the request and tracks your ongoing access. See [[what is an approval|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]].`,
              },
              {
                id: 'entity-confirmation',
                icon: 'clock-check',
                title: 'Confirmation',
                text: `A periodic check-in to verify you're still using an approved product. Confirming takes seconds and keeps your approval active. See [[what are confirmations|${FAQ_ARTICLE_IDS.WHAT_ARE_CONFIRMATIONS}]].`,
              },
            ],
          },
        ],
      },
      {
        id: 'staying-informed-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'How you stay informed',
        icon: 'bell-ring',
        color: 'positive',
        blocks: [
          {
            id: 'staying-informed-checklist',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            items: [
              {
                id: 'channel-in-app',
                text: '**In-app notifications** when a status changes, a message arrives, or a confirmation is due.',
              },
              {
                id: 'channel-messages',
                text: '**Messages** on each request - a direct line to the reviewing administrator.',
              },
              {
                id: 'channel-slack',
                text: `**Slack integration** if your organization has it enabled - [[connect it|${FAQ_ARTICLE_IDS.INTEGRATIONS}]], then [[choose which updates reach you|${FAQ_ARTICLE_IDS.SET_UP_NOTIFICATIONS}]] there.`,
              },
              {
                id: 'channel-email',
                text: `**Email notifications** about your requests and approvals - [[choose which ones you get|${FAQ_ARTICLE_IDS.SET_UP_NOTIFICATIONS}]] in **Settings** → **Notifications**.`,
              },
            ],
          },
        ],
      },
    ],
    faqs: [
      {
        id: 'request-vs-approval',
        question: "What's the difference between a request and an approval?",
        answer:
          'A request tracks the review process and ends when a decision is made. An approval only exists if your request was accepted - it tracks your ongoing access from then on.\nOne request leads to either one approval or none. Once the request closes, the approval is what matters.',
      },
      {
        id: 'do-all-three-apply',
        question: 'Do confirmations apply to every product?',
        answer: `Each approval has its own due date. Some approvals never need confirming.\nAn empty **Product confirmations** list means nothing is due right now.`,
      },
      {
        id: 'what-if-i-miss-confirmation',
        question: 'What happens if I miss a confirmation?',
        answer: `You'll get reminders first, but if you don't confirm within the required window your approval may be revoked.\nIf that happens, you can [[submit a new request|${FAQ_ARTICLE_IDS.HOW_TO_SUBMIT}]] to regain access.`,
      },
    ],
  },

  [FAQ_ARTICLE_IDS.YOUR_HOME_DASHBOARD]: {
    title: 'Your home dashboard',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `Your home dashboard is your starting point - a quick way to see where your [[requests|${FAQ_ARTICLE_IDS.WHAT_IS_A_REQUEST}]] stand, whether any [[confirmations|${FAQ_ARTICLE_IDS.WHAT_ARE_CONFIRMATIONS}]] need attention, and what's changed recently.`,
      },
      {
        id: 'stats-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Your activity at a glance',
        icon: 'bar-chart',
        color: 'brand',
        blocks: [
          {
            id: 'stats-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `A summary of your request history: total submitted, under review, approved, and denied.\nThe approved count also shows how many [[approvals|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]] are currently **active** - meaning you have valid access to those products right now. That number can be lower if any approvals were later revoked.`,
          },
        ],
      },
      {
        id: 'banners-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Quick actions',
        icon: 'bell',
        color: 'cyan',
        blocks: [
          {
            id: 'banners-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `Start a new request anytime: {btn:request-approval}.\nWhen [[confirmations|${FAQ_ARTICLE_IDS.WHAT_ARE_CONFIRMATIONS}]] are due, the dashboard alerts you so you can act before your access is affected.`,
          },
        ],
      },
      {
        id: 'cards-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'What needs your attention',
        icon: 'layout-panel-top',
        color: 'info',
        blocks: [
          {
            id: 'cards-journey',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.JOURNEY,
            items: [
              {
                id: 'card-under-review',
                icon: 'hourglass',
                title: 'Requests under review',
                text: 'Waiting on a decision? Open any request to check its status or message the administrator.',
              },
              {
                id: 'card-due-confirmation',
                icon: 'bell-ring',
                title: 'Approvals due for confirmation',
                text: `Approvals that need a periodic [[confirmation|${FAQ_ARTICLE_IDS.WHAT_ARE_CONFIRMATIONS}]] surface here. Confirming takes seconds.`,
              },
              {
                id: 'card-recent-approvals',
                icon: 'badge-check',
                title: 'Recent approvals',
                text: 'Your newest approvals, so you know what access was recently granted.',
              },
              {
                id: 'card-drafts',
                icon: 'file-edit',
                title: 'Draft requests',
                text: `Started but didn't finish? Pick up any [[draft|${FAQ_ARTICLE_IDS.WHAT_IS_A_DRAFT}]] where you left off.`,
              },
            ],
          },
          {
            id: 'cards-note',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: 'These are previews. For full lists with filtering and sorting, use **Product requests**, **Product approvals**, or **Product confirmations** in the left menu.',
          },
        ],
      },
    ],
    faqs: [
      {
        id: 'no-confirmations-visible',
        question: "Why don't I see any confirmations on my dashboard?",
        answer: `None of your approvals are currently due. When a due date arrives, they'll appear automatically. See [[what are confirmations|${FAQ_ARTICLE_IDS.WHAT_ARE_CONFIRMATIONS}]].`,
      },
      {
        id: 'active-number-meaning',
        question: "What does the 'Active' number mean?",
        answer: `It shows how many approved products you currently have valid access to. Approvals can be revoked over time, so the active count may be lower than total approved. See [[what is an approval|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]].`,
      },
    ],
  },

  // ── Submitting a Request ───────────────────────────────────────────────
  [FAQ_ARTICLE_IDS.WHAT_IS_A_REQUEST]: {
    title: 'What is a request?',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `A request is how you ask for access to an AI product. You can start one from **Product requests** or from your [[Home dashboard|${FAQ_ARTICLE_IDS.YOUR_HOME_DASHBOARD}]].\nAn administrator reviews it and either approves it (creating an [[approval|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]]) or denies it.`,
      },
      {
        id: 'where-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Where you see your requests',
        icon: 'eye',
        color: 'brand',
        blocks: [
          {
            id: 'where-grid',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.GRID,
            items: [
              {
                id: 'bullet-product-requests',
                icon: 'clipboard-clock',
                title: 'Product requests',
                text: 'Your main list of all requests. Filter by status - Draft, Under review, Approved, or Denied - to find what you need.',
                jumpTo: { name: ROUTES.TICKETS },
              },
              {
                id: 'bullet-home',
                icon: 'layout-panel-top',
                title: 'Home dashboard',
                text: `A quick snapshot of what needs your attention: requests under review, recent drafts, and shortcuts to take action.`,
                jumpTo: { name: ROUTES.HOME },
              },
            ],
          },
        ],
      },
      {
        id: 'tabs-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'What you see when you open a request',
        icon: 'layout-dashboard',
        color: 'positive',
        blocks: [
          {
            id: 'tabs-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: 'When you open a request, you get four tabs to help you stay on top of it',
          },
          {
            id: 'tabs-journey',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.JOURNEY,
            items: [
              {
                id: 'tab-information',
                icon: 'info',
                title: 'Information',
                text: 'An overview of your request: the product, current status, key dates, and any available actions.',
              },
              {
                id: 'tab-details',
                icon: 'clipboard-list',
                title: 'Details',
                text: 'The answers you provided in the questionnaire, so you can review exactly what you submitted.',
              },
              {
                id: 'tab-attachments',
                icon: 'paperclip',
                title: 'Attachments',
                text: `Any [[files you uploaded|${FAQ_ARTICLE_IDS.UPLOADING_FILES}]] to support your request. You can add more while the request is under review. After a decision, you can't add or remove files.`,
              },
              {
                id: 'tab-messages',
                icon: 'message-circle',
                title: 'Messages',
                text: `A conversation thread with the administrator reviewing your request. Use it to answer follow-up questions or provide extra context. Not available for [[drafts|${FAQ_ARTICLE_IDS.WHAT_IS_A_DRAFT}]] - messages start once you submit.`,
              },
            ],
          },
        ],
      },
      {
        id: 'statuses-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Request statuses',
        icon: 'circle-dot',
        color: 'info',
        blocks: [
          {
            id: 'statuses-grid',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.GRID,
            items: [
              {
                id: 'status-draft',
                statusKey: 'draft',
                text: `You've started the request but haven't submitted it yet. No one else can see it until you do. See [[what is a draft|${FAQ_ARTICLE_IDS.WHAT_IS_A_DRAFT}]].`,
              },
              {
                id: 'status-under-review',
                statusKey: 'processing',
                text: `Your request has been submitted and an administrator is reviewing it. They may reach out with follow-up questions. See [[tracking your request|${FAQ_ARTICLE_IDS.TRACKING_YOUR_REQUEST}]].`,
              },
              {
                id: 'status-approved',
                statusKey: 'approved',
                text: `Your request was accepted and an [[approval|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]] now tracks your access. See [[request approved|${FAQ_ARTICLE_IDS.REQUEST_APPROVED}]].`,
              },
              {
                id: 'status-denied',
                statusKey: 'denied',
                text: `Access wasn't granted this time. You can check the reason, message the administrator, or [[duplicate the request|${FAQ_ARTICLE_IDS.DUPLICATING_A_REQUEST}]] to try again. See [[request denied|${FAQ_ARTICLE_IDS.REQUEST_DENIED}]].`,
              },
            ],
          },
        ],
      },
    ],
    faqs: [
      {
        id: 'denied-reason-location',
        question: 'Where do I see the reason my request was denied?',
        answer:
          'Open the denied request from **Product requests** (Denied filter). The denial reason is shown in the request details, and the administrator may also have left context in the Messages thread.\nIf no reason was provided, you can send them a message directly from the request to ask.',
      },
      {
        id: 'edit-after-submit',
        question: 'Can I edit or cancel a request after submitting?',
        answer: `Once submitted, a request can't be edited or withdrawn. If you need to clarify something, use the Messages tab on the request to reach the administrator.\nIf you need to correct your answers, you can [[duplicate the request|${FAQ_ARTICLE_IDS.DUPLICATING_A_REQUEST}]] to create a new draft with your previous information pre-filled.`,
      },
      {
        id: 'after-decision',
        question: "What happens to my request after it's approved or denied?",
        answer: `The request closes either way - it becomes a record of what was submitted and decided.\nIf approved, see [[your request was approved|${FAQ_ARTICLE_IDS.REQUEST_APPROVED}]]. If denied, see [[your request was denied|${FAQ_ARTICLE_IDS.REQUEST_DENIED}]].`,
      },
    ],
  },

  [FAQ_ARTICLE_IDS.HOW_TO_SUBMIT]: {
    title: 'How to submit a request',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `Submitting a [[request|${FAQ_ARTICLE_IDS.WHAT_IS_A_REQUEST}]] is the starting point for getting access to an AI product.\nA guided form walks you through finding the product, answering your organization's questions, and optionally uploading supporting documents before you submit.`,
      },
      {
        id: 'steps-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Step by step',
        icon: 'clipboard-list',
        color: 'cta',
        blocks: [
          {
            id: 'steps',
            type: CONTENT_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'step-open',
                title: 'Start a request',
                text: 'Click {btn:request-approval} - you can find it in the **left menu**, on your **Home dashboard**, or on the **Product requests** page.',
                jumpTo: {
                  name: ROUTES.TICKET_EDIT,
                  params: { ticketStep: TICKET_EDIT_STEPS.PRODUCT },
                },
              },
              {
                id: 'step-find',
                title: 'Find the product',
                text: `In the **Find** step, search by product or company name and select the AI product you want access to.\nDon't see it in the catalog? You can use the **Custom Entry** tab to request a product that isn't listed yet, and add a website so it can be identified. See [[what if my product isn't listed?|${FAQ_ARTICLE_IDS.PRODUCT_NOT_IN_CATALOG}]].`,
              },
              {
                id: 'step-explain',
                title: 'Explain and attach',
                text: `In the **Explain** step, answer your organization's questionnaire under **Request Details**. Some questions ask you to attach a file - those uploads are saved to the draft automatically. If needed, switch to the **Request Attachments** tab to upload other supporting documents. See [[uploading files|${FAQ_ARTICLE_IDS.UPLOADING_FILES}]].\nYou can use the **Request preview** panel on the right to check your progress at any time.\nClick {btn:save-draft} to save your work as a [[draft|${FAQ_ARTICLE_IDS.WHAT_IS_A_DRAFT}]] and come back later.`,
              },
              {
                id: 'step-submit',
                title: 'Submit',
                text: 'When you are ready, click {btn:submit-request} to send your request for review.',
              },
            ],
          },
        ],
      },
      {
        id: 'after-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'What happens after you submit',
        icon: 'clock',
        color: 'info',
        blocks: [
          {
            id: 'after-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `Your request moves to {status:processing} and appears in **Product requests**. You can [[track your request|${FAQ_ARTICLE_IDS.TRACKING_YOUR_REQUEST}]] from there.\nAn administrator will review it and may send follow-up questions through the Messages tab.`,
          },
          {
            id: 'after-note',
            type: CONTENT_BLOCK_TYPES.NOTE,
            icon: 'alert-circle',
            label: 'Important',
            text: `Once submitted, a request can't be edited. If you need to clarify something, message the administrator from the request. If you need to correct your answers, [[duplicate the request|${FAQ_ARTICLE_IDS.DUPLICATING_A_REQUEST}]] to create a new draft.`,
          },
        ],
      },
    ],
    faqs: [
      {
        id: 'review-notification',
        question: 'Will I be notified when my request is reviewed?',
        answer: `Yes - you'll get an in-app notification whenever something happens: the status changes, the administrator makes a decision, or they send you a message.\nYour organization may also send notifications through other channels. See [[how you stay informed|${FAQ_ARTICLE_IDS.KEY_CONCEPTS}]] for details.`,
      },
      {
        id: 'multiple-requests',
        question: 'Can I have multiple requests in progress at once?',
        answer:
          "Absolutely. There's no limit - you can submit requests for different AI products at the same time, and each one goes through its own independent review.\nThis is useful if you need access to several products for a project. Each request is tracked separately, so one being approved or denied has no effect on the others.",
      },
    ],
  },

  [FAQ_ARTICLE_IDS.WHAT_IS_A_DRAFT]: {
    title: 'What is a draft?',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `A draft is a [[request|${FAQ_ARTICLE_IDS.WHAT_IS_A_REQUEST}]] you've started but haven't submitted yet - your work in progress.\nYou can save at any point and come back later to finish without losing what you've already entered.`,
      },
      {
        id: 'what-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Only you can see it',
        icon: 'file-edit',
        color: 'brand',
        blocks: [
          {
            id: 'what-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: "A draft shows as {status:draft} and stays completely private - no administrator sees it and it doesn't enter the review queue. You're in control of when to submit.\nWhen you're ready, click {btn:submit-request} to send it for review.",
          },
        ],
      },
      {
        id: 'save-resume-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Saving and finding your drafts',
        icon: 'save',
        color: 'positive',
        blocks: [
          {
            id: 'save-resume-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `To save your progress, click {btn:save-draft} at any point while filling out the form. If you try to leave with unsaved changes, we'll remind you to save before you go.\nTo pick up where you left off, find your draft in **Product requests** (Draft filter) or in the **Your draft requests** card on your [[Home dashboard|${FAQ_ARTICLE_IDS.YOUR_HOME_DASHBOARD}]]. Opening it takes you straight back to the form.`,
          },
        ],
      },
      {
        id: 'delete-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Deleting a draft',
        icon: 'trash',
        color: 'negative',
        blocks: [
          {
            id: 'delete-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: "If you no longer need a draft, open it and click **Delete Draft** on the form. You'll be asked to confirm before anything is removed, so there's no risk of deleting it by accident.",
          },
        ],
      },
      {
        id: 'Note - expiry',
        type: CONTENT_BLOCK_TYPES.NOTE,
        icon: 'calendar',
        label: 'Note',
        text: "Drafts don't expire automatically, but your organization may clean up old ones periodically. It's best to complete and submit your request once you have the information you need.",
      },
    ],
    faqs: [
      {
        id: 'multiple-drafts',
        question: 'Can I have multiple drafts?',
        answer:
          "Absolutely. You can have several drafts going at the same time - for example, one for each AI product you're considering. Each draft is independent and can be edited or submitted whenever you're ready.",
      },
      {
        id: 'unsaved-changes',
        question: 'What happens if I leave the form without saving?',
        answer:
          "You'll see a reminder that your changes haven't been saved yet. From there you can go back to save your work, or discard and leave.\nMake sure to click {btn:save-draft} regularly while working on your request so nothing is lost.",
      },
      {
        id: 'draft-to-submitted',
        question: 'Can I turn a submitted request back into a draft?',
        answer: `No - once a request is submitted, it can't be reverted to a draft. If you need to make changes, you can [[duplicate the request|${FAQ_ARTICLE_IDS.DUPLICATING_A_REQUEST}]] to create a new draft with your previous answers pre-filled, then edit and resubmit.`,
      },
    ],
  },

  [FAQ_ARTICLE_IDS.UPLOADING_FILES]: {
    title: 'Uploading files',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `You can attach supporting documents to your [[request|${FAQ_ARTICLE_IDS.WHAT_IS_A_REQUEST}]] - things like a business case, a vendor security questionnaire, or a manager sign-off.\nAttachments are optional, but they often help the reviewer understand your use case and reach a decision faster.`,
      },
      {
        id: 'steps-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Step by step',
        icon: 'clipboard-list',
        color: 'cta',
        blocks: [
          {
            id: 'steps',
            type: CONTENT_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'step-where',
                title: 'Find the attachments tab',
                text: `While creating or editing a [[draft|${FAQ_ARTICLE_IDS.WHAT_IS_A_DRAFT}]], switch to the **Request Attachments** tab in the **Explain** step.\nAlready submitted? Open the request and click {btn:add-attachments} to add more files while it is under review.`,
              },
              {
                id: 'step-upload',
                title: 'Upload your files',
                text: 'Drag and drop files into the upload area, or click {btn:browse-file} to pick one from your device.',
              },
              {
                id: 'step-limits',
                title: 'File requirements',
                text: "Each file can be up to **45MB**. Most common formats are accepted - PDFs, images, spreadsheets, Word documents, and more.\nExecutable files and scripts are blocked for security reasons. If a file type isn't allowed, you'll see an error when you try to upload.",
              },
            ],
          },
          {
            id: 'under-review-note',
            type: CONTENT_BLOCK_TYPES.NOTE,
            icon: 'info',
            label: 'Tip',
            text: "Right after submission, the {btn:add-attachments} button may be temporarily disabled while the request is being processed. That's normal - wait a moment and it will become available once the request moves to under review.\nOnce a decision is made, you can't upload any more files.",
          },
        ],
      },
      {
        id: 'file-questions',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'File questions',
        icon: 'upload-cloud',
        color: 'brand',
        blocks: [
          {
            id: 'file-questions-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `Some questions under **Request Details** ask you to attach one or more files. Uploading a file answers that question.\nThe file is saved to your [[draft|${FAQ_ARTICLE_IDS.WHAT_IS_A_DRAFT}]] as soon as the upload finishes - you don't need to click {btn:save-draft} for it to stick. While the request is still a draft, you can remove it from the question if you attached the wrong file.`,
          },
        ],
      },
      {
        id: 'removing-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Removing files',
        icon: 'trash',
        color: 'negative',
        blocks: [
          {
            id: 'removing-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `You can delete files while the request is still a [[draft|${FAQ_ARTICLE_IDS.WHAT_IS_A_DRAFT}]] - from **Request Attachments**, or from the file question they belong to.\nOnce submitted, files can't be removed by you. If you need a file taken down after submission, message the administrator from the request.\nAfter a decision is made, you also can't upload any more files.`,
          },
        ],
      },
      {
        id: 'Note - duplicate',
        type: CONTENT_BLOCK_TYPES.NOTE,
        icon: 'alert-circle',
        label: 'Note',
        text: `When you [[duplicate a request|${FAQ_ARTICLE_IDS.DUPLICATING_A_REQUEST}]], your answers carry over but attachments don't - you'll need to re-upload any supporting files in the new draft.`,
      },
    ],
    faqs: [
      {
        id: 'file-visibility',
        question: 'Who can see the files I upload?',
        answer:
          "Only you and the administrators reviewing your request. Other employees in your organization can't see or access your attachments.",
      },
      {
        id: 'file-types',
        question: 'What file types can I upload?',
        answer:
          "Most common formats are accepted - PDFs, images, Word documents, spreadsheets, presentations, and more. Executable files and scripts (like .exe, .bat, .js, .py) are blocked for security reasons.\nIf a file type isn't accepted, you'll see an error when you try to upload it. In that case, try converting the file to PDF or another supported format.",
      },
      {
        id: 'file-after-submit',
        question: 'Can I add or remove files after submitting?',
        answer: `You can add more files while the request is under review - open the request and click {btn:add-attachments}.\nYou can't remove files after you submit. If you need one taken down, use the Messages tab to ask the administrator.\nOnce a decision is made, you can't upload more files either.`,
      },
    ],
  },

  // ── After You Submit ───────────────────────────────────────────────────
  [FAQ_ARTICLE_IDS.TRACKING_YOUR_REQUEST]: {
    title: 'Tracking your request',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `Once you've submitted a [[request|${FAQ_ARTICLE_IDS.WHAT_IS_A_REQUEST}]], you don't have to wonder what's happening with it. You can check on it anytime, and the platform will notify you when something changes - whether the administrator makes a decision or sends you a message.`,
      },
      {
        id: 'steps-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'How to check on a request',
        icon: 'clipboard-list',
        color: 'cta',
        blocks: [
          {
            id: 'steps',
            type: CONTENT_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'step-go',
                title: 'Go to Product requests',
                text: `Open **Product requests** from the left menu, or use the request cards on your [[Home dashboard|${FAQ_ARTICLE_IDS.YOUR_HOME_DASHBOARD}]] for a quick shortcut.`,
                jumpTo: { name: ROUTES.TICKETS },
              },
              {
                id: 'step-filter',
                title: 'Find the request you need',
                text: 'Filter by **Under review**, **Approved**, or **Denied** to narrow things down. You can also search by product name if you have several requests.',
              },
              {
                id: 'step-open',
                title: 'Open the request',
                text: `Click on the request to see its current status, review your [[attachments|${FAQ_ARTICLE_IDS.UPLOADING_FILES}]], and read or reply to messages from the administrator.`,
              },
              {
                id: 'step-tabs',
                title: 'Stay in touch',
                text: "If the administrator has questions, they'll reach out through the **Messages** tab on the request. Replying there is the fastest way to keep the review moving.",
              },
            ],
          },
        ],
      },
      {
        id: 'messages-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Communicating with the reviewer',
        icon: 'message-circle',
        color: 'info',
        blocks: [
          {
            id: 'messages-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: "The **Messages** tab on your request is a direct line to the administrator reviewing it. If they need clarification or additional context, they'll reach out there - and you can reply in the same thread.\nResponding promptly helps avoid delays in the review process.",
          },
        ],
      },
      {
        id: 'notifications-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'How you get notified',
        icon: 'bell',
        color: 'cyan',
        blocks: [
          {
            id: 'notifications-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `You'll receive an in-app notification whenever your request status changes or the administrator sends you a message - so you don't need to keep checking manually.\nSee [[how you stay informed|${FAQ_ARTICLE_IDS.KEY_CONCEPTS}]] for the full list of notification channels, including email and [[Slack|${FAQ_ARTICLE_IDS.INTEGRATIONS}]].`,
          },
        ],
      },
      {
        id: 'Note - processing',
        type: CONTENT_BLOCK_TYPES.NOTE,
        icon: 'hourglass',
        label: 'Note',
        text: 'Right after you submit, the request goes through a brief processing period. During that time, some actions like sending messages or adding attachments may be temporarily unavailable. This is normal and only lasts a moment.',
      },
    ],
    faqs: [
      {
        id: 'review-timeline',
        question: 'How long does the review usually take?',
        answer:
          "It depends on your organization's processes and how many requests are in the queue. You'll be notified as soon as something changes, so there's no need to keep checking back.",
      },
      {
        id: 'admin-questions',
        question: 'Will I know if the administrator has questions?',
        answer:
          "Yes - you'll get a notification when they send you a message. Open the request and check the **Messages** tab to read and reply. The faster you respond, the smoother the review goes.",
      },
      {
        id: 'speed-up-review',
        question: 'Can I do anything to speed up the review?',
        answer: `The review timeline is up to your organization, but you can help things move along by responding quickly to any messages from the administrator and making sure your [[attachments|${FAQ_ARTICLE_IDS.UPLOADING_FILES}]] and answers are complete.`,
      },
    ],
  },

  [FAQ_ARTICLE_IDS.REQUEST_APPROVED]: {
    title: 'Your request was approved',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `Your [[request|${FAQ_ARTICLE_IDS.WHAT_IS_A_REQUEST}]] was accepted - **you're authorized to use the product** for your work.\nThe request closes and a separate [[approval|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]] takes over to track your access going forward.`,
      },
      {
        id: 'view-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Where to find your approval',
        icon: 'external-link',
        color: 'positive',
        blocks: [
          {
            id: 'view-grid',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.GRID,
            items: [
              {
                id: 'bullet-from-request',
                icon: 'file-text',
                title: 'From the approved request',
                text: 'Filter **Product requests** to Approved, open the request, and click {btn:show-approval} to jump to the approval.',
                jumpTo: { name: ROUTES.TICKETS, query: { status: 'approved' } },
              },
              {
                id: 'bullet-from-list',
                icon: 'shield-check',
                title: 'From Product approvals',
                text: 'Go to **Product approvals** to see all your approvals. Open any one for details, status, and conditions.',
                jumpTo: { name: ROUTES.APPROVALS },
              },
            ],
          },
        ],
      },
      {
        id: 'conditions-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Conditions and next steps',
        icon: 'clipboard-list',
        color: 'info',
        blocks: [
          {
            id: 'conditions-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `Your administrator may have attached **conditions** to the approval. Review them in your [[approval details|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]] and follow them for as long as the approval is active.\nSome organizations also require periodic [[confirmations|${FAQ_ARTICLE_IDS.WHAT_ARE_CONFIRMATIONS}]] to verify you're still using the product.`,
          },
        ],
      },
      {
        id: 'Note - access',
        type: CONTENT_BLOCK_TYPES.NOTE,
        icon: 'info',
        label: 'Tip',
        text: 'Approval means your organization has **authorized access**. The actual tool setup - license, account provisioning - depends on your **IT team** and may happen separately.',
      },
    ],
    faqs: [
      {
        id: 'immediate-access',
        question: 'Does approval mean I can use the tool right away?',
        answer:
          "Approval means **you're authorized**, but the actual setup - license, account access - depends on your **IT team** and may take a bit longer. If you're unsure, check with them directly.",
      },
      {
        id: 'approval-conditions',
        question: 'What are approval conditions?',
        answer: `Guidelines or restrictions your administrator may attach to ensure the product is used responsibly. See [[approval conditions|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]] for details on what they mean and where to find them.`,
      },
      {
        id: 'approval-revoked',
        question: 'What happens if my approval is revoked later?',
        answer: `If your access is revoked, **you'll be notified** and can check the reason in the **approval details**.\nStill need access? [[Duplicate the original request|${FAQ_ARTICLE_IDS.DUPLICATING_A_REQUEST}]] to start a new one. See [[active vs revoked|${FAQ_ARTICLE_IDS.ACTIVE_VS_REVOKED}]] for more.`,
      },
    ],
  },

  [FAQ_ARTICLE_IDS.REQUEST_DENIED]: {
    title: 'Your request was denied',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `A denial doesn't mean never - it means the [[request|${FAQ_ARTICLE_IDS.WHAT_IS_A_REQUEST}]] wasn't approved this time. The request closes, **no** [[approval|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]] **is created**, and you'll be notified.\nYou can find the denied request in **Product requests** (Denied filter) to see the reason and what to do next.`,
      },
      {
        id: 'understand-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Understanding the denial',
        icon: 'search',
        color: 'info',
        blocks: [
          {
            id: 'understand-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: 'Open the denied request to check two things',
          },
          {
            id: 'understand-journey',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.JOURNEY,
            items: [
              {
                id: 'understand-reason',
                icon: 'file-text',
                title: 'Denial reason',
                text: 'Shown in the request details - this explains why the administrator declined.',
              },
              {
                id: 'understand-messages',
                icon: 'message-circle',
                title: 'Messages',
                text: 'The administrator may have left additional context or suggestions in the conversation thread.',
              },
              {
                id: 'understand-ask',
                icon: 'send',
                title: 'Still unclear?',
                text: '**Send a message** directly from the request to ask the administrator for clarification.',
              },
            ],
          },
        ],
      },
      {
        id: 'what-you-can-do-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'What you can do next',
        icon: 'clipboard-paste',
        color: 'brand',
        blocks: [
          {
            id: 'duplicate-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `If you still need access, you can [[duplicate the request|${FAQ_ARTICLE_IDS.DUPLICATING_A_REQUEST}]] to create a new draft with your previous answers pre-filled, update what's needed, and resubmit for a fresh review.`,
          },
          {
            id: 'appeal-note',
            type: CONTENT_BLOCK_TYPES.NOTE,
            icon: 'info',
            label: 'Tip',
            text: `There's no formal appeal process, but you can always use the **Messages** tab on the request to discuss the decision with the administrator, or [[contact support|${FAQ_ARTICLE_IDS.CONTACTING_SUPPORT}]] if you need help.`,
          },
        ],
      },
    ],
    faqs: [
      {
        id: 'reapply-after-denial',
        question: 'Can I re-apply after a denial?',
        answer: `Yes. Click {btn:duplicate-request} on the denied request to start a new draft. See [[duplicating a request|${FAQ_ARTICLE_IDS.DUPLICATING_A_REQUEST}]] for the full walkthrough.`,
      },
      {
        id: 'no-reason-provided',
        question: 'What if no denial reason was provided?',
        answer: `Open the denied request and check the **Messages** tab - the administrator may have left context there. If not, you can **send them a message** directly from the request to ask.`,
      },
      {
        id: 'same-reviewer',
        question: 'Will the same administrator review my new request?',
        answer:
          'That depends on how your organization assigns reviewers. Your new request goes through the standard review process, which may or may not involve the same person.',
      },
    ],
  },

  [FAQ_ARTICLE_IDS.DUPLICATING_A_REQUEST]: {
    title: 'Duplicating a request',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `Need to try again? Duplicating creates a **new draft** from an existing request - same product, and you choose whether to carry over your previous answers.\nIt's the fastest way to re-apply after a [[denial|${FAQ_ARTICLE_IDS.REQUEST_DENIED}]] or when a [[revoked approval|${FAQ_ARTICLE_IDS.ACTIVE_VS_REVOKED}]] means you need access again.`,
      },
      {
        id: 'when-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'When you can duplicate',
        icon: 'copy',
        color: 'brand',
        blocks: [
          {
            id: 'when-grid',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.GRID,
            items: [
              {
                id: 'bullet-denied',
                icon: 'badge-x',
                title: 'After a denial',
                text: 'Open the denied request and click {btn:duplicate-request} to re-apply with updated information.',
                jumpTo: { name: ROUTES.TICKETS, query: { status: 'denied' } },
              },
              {
                id: 'bullet-revoked',
                icon: 'shield-alert',
                title: 'After a revocation',
                text: 'Open the revoked approval and click {btn:duplicate-request} to request access to the same product again.',
                jumpTo: { name: ROUTES.APPROVALS },
              },
            ],
          },
        ],
      },
      {
        id: 'steps-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'How it works',
        icon: 'clipboard-list',
        color: 'cta',
        blocks: [
          {
            id: 'steps',
            type: CONTENT_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'step-trigger',
                title: 'Click duplicate',
                text: 'Open the denied request or revoked approval and click {btn:duplicate-request}.',
              },
              {
                id: 'step-choose',
                title: 'Choose what to carry over',
                text: "By default, your **previous answers are included** so you don't start from scratch. You can uncheck this if you'd rather start fresh. Either way, you'll be able to edit everything before submitting.",
              },
              {
                id: 'step-confirm',
                title: 'Review and submit',
                text: `Click {btn:duplicate-and-review} to create the new draft. A banner will remind you to review the pre-filled answers. Update what you need and [[submit|${FAQ_ARTICLE_IDS.HOW_TO_SUBMIT}]] when ready.`,
              },
            ],
          },
        ],
      },
      {
        id: 'Note - attachments',
        type: CONTENT_BLOCK_TYPES.NOTE,
        icon: 'alert-circle',
        label: 'Important',
        text: `**File attachments are not copied.** You'll need to re-upload any supporting documents in the new draft. See [[uploading files|${FAQ_ARTICLE_IDS.UPLOADING_FILES}]].`,
      },
    ],
    faqs: [
      {
        id: 'duplicate-approved',
        question: 'Can I duplicate an approved request?',
        answer: `No - duplicating is only available for **denied requests** and **revoked approvals**. If your request was approved, you already have an [[approval|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]] tracking your access.`,
      },
      {
        id: 'change-product',
        question: 'Can I change the product when duplicating?',
        answer: `No - the duplicate is tied to the **same product** as the original request. If you need access to a different product, [[submit a new request|${FAQ_ARTICLE_IDS.HOW_TO_SUBMIT}]] instead.`,
      },
      {
        id: 'new-request-reference',
        question: 'Will my new request reference the old one?',
        answer:
          "Yes - when you open the duplicated draft, you'll see a **banner** noting it was created from a previous submission. This is just a reminder to review your answers before submitting.",
      },
    ],
  },

  // ── Approvals ─────────────────────────────────────────────────────────
  [FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL]: {
    title: 'What is an approval?',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `An approval means you've been **granted access** to an AI product. It's created when an administrator accepts your [[request|${FAQ_ARTICLE_IDS.WHAT_IS_A_REQUEST}]] - the request closes, and the approval takes over as the record of your ongoing access.\nAn approval is **separate from the request** that created it. It can be [[active or revoked|${FAQ_ARTICLE_IDS.ACTIVE_VS_REVOKED}]], and your organization may require periodic [[confirmations|${FAQ_ARTICLE_IDS.WHAT_ARE_CONFIRMATIONS}]] to maintain compliance.`,
      },
      {
        id: 'where-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Where you find your approvals',
        icon: 'eye',
        color: 'brand',
        blocks: [
          {
            id: 'where-grid',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.GRID,
            items: [
              {
                id: 'bullet-product-approvals',
                icon: 'shield-check',
                title: 'Product approvals',
                text: 'Your main list of all approvals. Filter by Active or Revoked to find what you need.',
                jumpTo: { name: ROUTES.APPROVALS },
              },
              {
                id: 'bullet-from-request',
                icon: 'file-text',
                title: 'From an approved request',
                text: 'Open any approved request and click {btn:show-approval} to jump straight to the approval.',
              },
            ],
          },
        ],
      },
      {
        id: 'whats-in-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Staying on top of your access',
        icon: 'shield-check',
        color: 'positive',
        blocks: [
          {
            id: 'whats-in-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `Your approval is where you check on your access: which product was approved, when it was granted, your next [[confirmation|${FAQ_ARTICLE_IDS.WHAT_ARE_CONFIRMATIONS}]] due date, and any conditions that apply.\nIt's also where you take action - {btn:revoke-approval} if you no longer need the tool, or {btn:duplicate-request} to [[re-apply|${FAQ_ARTICLE_IDS.DUPLICATING_A_REQUEST}]] if access was [[revoked|${FAQ_ARTICLE_IDS.ACTIVE_VS_REVOKED}]].`,
          },
        ],
      },
      {
        id: 'conditions-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Approval conditions',
        icon: 'clipboard-list',
        color: 'info',
        blocks: [
          {
            id: 'conditions-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: 'An administrator may attach conditions to your approval - for example, usage restrictions, data handling rules, or compliance requirements your organization needs you to follow.\nThese conditions exist to **protect both you and your organization**, so it is important to review them carefully and **follow them for as long as the approval is active**. Not every approval has conditions - if none were set, the approval will note that.',
          },
        ],
      },
    ],
    faqs: [
      {
        id: 'request-vs-approval',
        question: "What's the difference between an approval and a request?",
        answer: `A [[request|${FAQ_ARTICLE_IDS.WHAT_IS_A_REQUEST}]] is how you ask for access - it tracks the review process and closes once a decision is made. An approval is the result: it only exists if your request was accepted, and it tracks your ongoing access from that point on.\nThink of it this way: one request leads to either one approval (if accepted) or none (if denied).`,
      },
      {
        id: 'approval-conditions-meaning',
        question: 'What happens if my approval has conditions?',
        answer:
          "Conditions are requirements set by your administrator to ensure the tool is used in line with your organization's **security and compliance policies** - for example, restrictions on what data you can process or how you store outputs.\nThey are shown in the approval so you can reference them at any time. **Following them is important**: non-compliance could lead to your approval being revoked.",
      },
    ],
  },

  [FAQ_ARTICLE_IDS.ACTIVE_VS_REVOKED]: {
    title: 'Active vs. revoked approvals',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `Every [[approval|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]] is in one of two states: **Active** means you have authorized access to the AI product, and **Revoked** means that access has been removed.\nUnderstanding the difference helps you know where you stand and what to do next.`,
      },
      {
        id: 'active-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Active',
        icon: 'shield-check',
        color: 'positive',
        blocks: [
          {
            id: 'active-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: 'An active approval means your access is **approved and compliant** - you are authorized to use the tool under the terms your organization set.',
          },
          {
            id: 'active-checklist',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            title: 'What this means for you',
            items: [
              {
                id: 'active-check-authorized',
                text: 'You can use the approved AI product as described in your request.',
              },
              {
                id: 'active-check-conditions',
                text: `If your approval has **conditions**, make sure you're following them - they're there to keep your usage aligned with your organization's policies.`,
              },
              {
                id: 'active-check-confirmation',
                text: `No action needed unless a [[confirmation|${FAQ_ARTICLE_IDS.WHAT_ARE_CONFIRMATIONS}]] is due - you'll be notified when it is.`,
              },
              {
                id: 'active-check-revoke',
                text: `If you **no longer need the tool**, you can {btn:revoke-approval} at any time. See [[how to revoke|${FAQ_ARTICLE_IDS.HOW_TO_REVOKE}]].`,
              },
            ],
          },
        ],
      },
      {
        id: 'revoked-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Revoked',
        icon: 'shield-alert',
        color: 'negative',
        blocks: [
          {
            id: 'revoked-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `A revoked approval means access to the tool has been removed. This can happen for several reasons - you may have revoked it yourself, or an administrator may have revoked it due to a policy change or a missed [[confirmation|${FAQ_ARTICLE_IDS.WHAT_ARE_CONFIRMATIONS}]].\nIf a reason was provided, you can check it in the approval along with who revoked it and when.`,
          },
          {
            id: 'revoked-reinstate-note',
            type: CONTENT_BLOCK_TYPES.NOTE,
            icon: 'alert-circle',
            label: 'Important',
            text: `**Revoked approvals cannot be reinstated.** If you need access again, you can {btn:duplicate-request} from the revoked approval to [[create a new draft|${FAQ_ARTICLE_IDS.DUPLICATING_A_REQUEST}]] and re-apply.`,
          },
        ],
      },
      {
        id: 'causes-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Why approvals get revoked',
        icon: 'alert-triangle',
        color: 'brand',
        blocks: [
          {
            id: 'causes-grid',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.GRID,
            items: [
              {
                id: 'cause-admin',
                icon: 'users',
                title: 'Administrator decision',
                text: "Your administrator may revoke access due to a **policy change**, tool deprecation, or because usage no longer aligns with your organization's requirements. A reason is usually provided.",
              },
              {
                id: 'cause-you',
                icon: 'user',
                title: 'You chose to revoke',
                text: `You can revoke your own approval at any time if you no longer need the tool. It's the responsible thing to do - it keeps your organization's access records accurate. See [[how to revoke|${FAQ_ARTICLE_IDS.HOW_TO_REVOKE}]].`,
              },
              {
                id: 'cause-confirmation',
                icon: 'bell-off',
                title: 'Missed confirmation',
                text: `If a [[confirmation|${FAQ_ARTICLE_IDS.WHAT_ARE_CONFIRMATIONS}]] goes unanswered, your administrator may revoke the approval. **Confirming on time** is the easiest way to prevent this.`,
              },
            ],
          },
        ],
      },
    ],
    faqs: [
      {
        id: 'revoked-reinstate',
        question: 'Can a revoked approval be reinstated?',
        answer: `No - once revoked, an approval cannot be restored. But you can always re-apply: click {btn:duplicate-request} on the revoked approval to [[create a new draft|${FAQ_ARTICLE_IDS.DUPLICATING_A_REQUEST}]] with your previous information, then submit it for a fresh review.`,
      },
      {
        id: 'who-can-revoke',
        question: 'Who can revoke my approval?',
        answer: `Either you or your administrator. You can [[revoke it yourself|${FAQ_ARTICLE_IDS.HOW_TO_REVOKE}]] at any time if you no longer need the tool. Your administrator can also revoke it - for example, if there's a policy change or if a required confirmation was missed.`,
      },
      {
        id: 'revoked-notification',
        question: 'Will I be notified if my approval is revoked?',
        answer:
          "Yes. You'll receive a notification when an approval is revoked, whether it was done by you or by your administrator. You can check the reason and details in the approval.",
      },
    ],
  },

  [FAQ_ARTICLE_IDS.HOW_TO_REVOKE]: {
    title: 'How to revoke an approval',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `If you no longer need access to an AI product, you can revoke the [[approval|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]] yourself. This lets your administrator know you're done with the tool and keeps your organization's **access records accurate**.\nThe approval stays in your history as [[revoked|${FAQ_ARTICLE_IDS.ACTIVE_VS_REVOKED}]] - you can always [[re-apply|${FAQ_ARTICLE_IDS.DUPLICATING_A_REQUEST}]] later if you need the tool again.`,
      },
      {
        id: 'steps-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Step by step',
        icon: 'clipboard-list',
        color: 'cta',
        blocks: [
          {
            id: 'steps',
            type: CONTENT_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'step-open',
                title: 'Open the approval',
                text: 'Go to **Product approvals** and open the approval you want to revoke.',
                jumpTo: { name: ROUTES.APPROVALS },
              },
              {
                id: 'step-revoke',
                title: 'Start the revocation',
                text: 'Click {btn:revoke-approval} at the bottom of the approval.',
              },
              {
                id: 'step-confirm',
                title: 'Confirm',
                text: 'Provide a reason for revoking - something like "project ended" or "switching to a different tool". It\'s optional, but it helps your administrator stay informed and keeps your organization\'s compliance records clear.\n**Once you confirm, the revocation takes effect immediately** and your administrator is notified.',
              },
            ],
          },
        ],
      },
      {
        id: 'Note - revoking',
        type: CONTENT_BLOCK_TYPES.NOTE,
        icon: 'alert-circle',
        label: 'Important',
        text: `**This action cannot be undone.** See [[active vs revoked|${FAQ_ARTICLE_IDS.ACTIVE_VS_REVOKED}]] for what happens next and how to re-apply if you change your mind.`,
      },
    ],
    faqs: [
      {
        id: 'access-after-revoke',
        question: 'What happens to my tool access after I revoke?',
        answer:
          "The platform records the revocation and your administrator is notified right away.\nWhen the tool is actually deprovisioned (for example, a license removed or account disabled) depends on your organization's IT process. If you're unsure whether access has been fully removed, contact your IT team.",
      },
      {
        id: 'reason-required',
        question: 'Do I have to give a reason when revoking?',
        answer:
          'No - the reason field is **optional**. But providing a brief note (for example, "project ended" or "switched to a different tool") helps your administrator understand why and keeps your organization\'s records clear.',
      },
    ],
  },

  // ── Confirmations ─────────────────────────────────────────────────────
  [FAQ_ARTICLE_IDS.WHAT_ARE_CONFIRMATIONS]: {
    title: 'What are confirmations?',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `A **confirmation** is a check that you're still using an AI tool you've been approved for.\nEach approval has its own due date. When one is due, you'll be notified so you can take care of it quickly. Some approvals never need confirming - they do not appear on **Product confirmations**.`,
      },
      {
        id: 'why-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Why confirmations matter',
        icon: 'list-checks',
        color: 'cyan',
        blocks: [
          {
            id: 'why-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `Confirmations help your organization keep track of who is actually using which tools. That information matters for managing licenses, running security reviews, and meeting compliance requirements.\nThe good news: confirming is quick. You just verify you're still using the tool, and your [[approval|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]] stays active and compliant.`,
          },
        ],
      },
      {
        id: 'where-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Where you see them',
        icon: 'eye',
        color: 'brand',
        blocks: [
          {
            id: 'where-grid',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.GRID,
            items: [
              {
                id: 'bullet-product-confirmations',
                icon: 'list-checks',
                title: 'Product confirmations',
                text: 'Your dedicated list of approvals that need confirming now. Confirm one by one or several at once. When nothing is due, this page shows your next confirmation date.',
                jumpTo: { name: ROUTES.CONFIRMATIONS },
              },
              {
                id: 'bullet-approval-view',
                icon: 'shield-check',
                title: 'Inside an approval',
                text: 'When a confirmation is due, a banner appears at the top of the approval so you can confirm right there.',
              },
              {
                id: 'bullet-home',
                icon: 'layout-panel-top',
                title: 'Home dashboard',
                text: 'An alert highlights how many approvals need confirming, with a shortcut to take care of them all.',
                jumpTo: { name: ROUTES.HOME },
              },
            ],
          },
        ],
      },
      {
        id: 'how-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'How to confirm',
        icon: 'check-circle',
        color: 'positive',
        blocks: [
          {
            id: 'how-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `Confirming takes just a moment - you verify that you're still using the tool.\nYou can confirm from **Product confirmations** (individually or in bulk), or directly from the approval. [[How to confirm an approval|${FAQ_ARTICLE_IDS.HOW_TO_CONFIRM}]] walks you through it step by step.`,
          },
          {
            id: 'how-note',
            type: CONTENT_BLOCK_TYPES.NOTE,
            icon: 'info',
            label: 'Tip',
            text: `No longer using the tool? [[Revoke the approval|${FAQ_ARTICLE_IDS.HOW_TO_REVOKE}]] instead of confirming - it keeps your access records accurate and frees up the license.`,
          },
        ],
      },
      {
        id: 'if-missed-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'If you do not confirm',
        icon: 'alert-triangle',
        color: 'negative',
        blocks: [
          {
            id: 'if-missed-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `Overdue confirmations stay visible on your [[home dashboard|${FAQ_ARTICLE_IDS.YOUR_HOME_DASHBOARD}]] and in **Product confirmations** so you won't forget.\nDepending on your organization's policy, an administrator may reach out or eventually revoke the approval - so it's best to confirm promptly or [[revoke|${FAQ_ARTICLE_IDS.HOW_TO_REVOKE}]] if you no longer need the tool.`,
          },
        ],
      },
    ],
    faqs: [
      {
        id: 'confirmation-due-date',
        question: 'Does confirming change when my next confirmation is due?',
        answer:
          "Yes. Confirming resets that approval's next due date. Other approvals keep their own dates.",
      },
      {
        id: 'all-approvals-require',
        question: 'Do all my approvals require confirmations?',
        answer:
          "No. Some approvals never need confirming. Those do not appear on **Product confirmations**, and you don't need to do anything.",
      },
      {
        id: 'no-longer-using',
        question: "What if I'm no longer using the tool?",
        answer: `If you no longer need access, [[revoke the approval|${FAQ_ARTICLE_IDS.HOW_TO_REVOKE}]] instead of confirming. This keeps your records accurate and lets your organization reclaim the license.\nYou can always [[re-apply|${FAQ_ARTICLE_IDS.DUPLICATING_A_REQUEST}]] later if you need the tool again.`,
      },
    ],
  },

  [FAQ_ARTICLE_IDS.HOW_TO_CONFIRM]: {
    title: 'How to confirm an approval',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `When a [[confirmation|${FAQ_ARTICLE_IDS.WHAT_ARE_CONFIRMATIONS}]] is due, it only takes a moment. You confirm that you're still using the tool.\nYou can confirm from **Product confirmations**, from the approval itself, or straight from your [[home dashboard|${FAQ_ARTICLE_IDS.YOUR_HOME_DASHBOARD}]]. If nothing is due, **Product confirmations** shows your next confirmation date.`,
      },
      {
        id: 'steps-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Step by step',
        icon: 'clipboard-list',
        color: 'cta',
        blocks: [
          {
            id: 'steps',
            type: CONTENT_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'step-go',
                title: 'Go to Product confirmations',
                text: `Open **Product confirmations** from the left menu. If you have confirmations due, you can also get there from the {btn:confirm-approvals} banner on your [[home dashboard|${FAQ_ARTICLE_IDS.YOUR_HOME_DASHBOARD}]].`,
                jumpTo: { name: ROUTES.CONFIRMATIONS },
              },
              {
                id: 'step-confirm',
                title: 'Confirm the approval',
                text: `You have two options: confirm directly from the list using the row actions menu and choosing {btn:confirm-approval}, or open the approval first to review it before confirming from there.`,
              },
              {
                id: 'step-dialog',
                title: "Verify you're still using the tool",
                text: "Confirm that you're still actively using the product. That's all it takes - that approval's next due date resets automatically and it drops off the list until then.",
              },
            ],
          },
        ],
      },
      {
        id: 'bulk-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Confirming several at once',
        icon: 'list-checks',
        color: 'cyan',
        blocks: [
          {
            id: 'bulk-steps',
            type: CONTENT_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'bulk-step-select',
                title: 'Select the approvals you want to confirm',
                text: "In **Product confirmations**, check off the approvals you're ready to confirm. Pick as many as you need.",
              },
              {
                id: 'bulk-step-confirm',
                title: 'Confirm them all at once',
                text: "Click {btn:confirm} from the toolbar and verify that you're still using the selected tools. Each confirmed approval drops off the list until its next due date.",
              },
            ],
          },
          {
            id: 'bulk-note',
            type: CONTENT_BLOCK_TYPES.NOTE,
            icon: 'info',
            label: 'Tip',
            text: 'Want to review an approval before confirming it? Open it individually from the row actions menu or approval view instead of selecting it in bulk.',
          },
        ],
      },
      {
        id: 'Note - revoking',
        type: CONTENT_BLOCK_TYPES.NOTE,
        icon: 'info',
        label: 'Tip',
        text: `No longer using one of these tools? [[Revoke the approval|${FAQ_ARTICLE_IDS.HOW_TO_REVOKE}]] instead of confirming - it keeps your access records accurate and frees up the license.`,
      },
    ],
    faqs: [
      {
        id: 'confirmation-extra-info',
        question: 'Does confirming require any additional information?',
        answer:
          "Usually no - you just confirm you're still using the tool and that's it.\nYour organization may optionally ask for a brief note when confirming; if so, you'll see the prompt in the dialog.",
      },
      {
        id: 'confirm-from-approval',
        question: 'Can I confirm from the approval page directly?',
        answer: `Yes. When a confirmation is due, a banner appears at the top of the [[approval|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]] with a {btn:confirm} button. You don't have to go through **Product confirmations** - you can confirm right there.`,
      },
    ],
  },

  // ── General ───────────────────────────────────────────────────────────
  [FAQ_ARTICLE_IDS.CONTACTING_SUPPORT]: {
    title: 'Contacting support',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: "If you can't find what you need in the Help Center, you can reach our support team directly from the platform. Just fill out a short form and they'll get back to you.\nThe more detail you include, the faster they can help.",
      },
      {
        id: 'steps-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Step by step',
        icon: 'clipboard-list',
        color: 'cta',
        blocks: [
          {
            id: 'steps',
            type: CONTENT_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'step-open',
                title: 'Open the form',
                text: 'Click {btn:contact-support} - you can find it in the **settings menu** (gear icon in the header), or in the **Help Center** (the "Need help?" card at the bottom of any page).',
              },
              {
                id: 'step-topic',
                title: 'Choose a topic',
                text: 'Pick what best describes your situation: **I have a question**, **Report an issue**, **Report a bug**, **Request a feature**, or **Something else**.',
              },
              {
                id: 'step-message',
                title: 'Describe your issue',
                text: 'Write a clear description of what you need help with. The more context you give, the easier it is for the team to assist you.',
              },
              {
                id: 'step-submit',
                title: 'Submit',
                text: "Click **Submit** and you're done. The support team will follow up with you.",
              },
            ],
          },
        ],
      },
      {
        id: 'what-to-include-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Tips for a faster response',
        icon: 'file-text',
        color: 'info',
        blocks: [
          {
            id: 'what-checklist',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            title: 'Include in your message',
            items: [
              {
                id: 'bullet-description',
                text: 'A clear description of the problem or question.',
              },
              {
                id: 'bullet-ids',
                text: 'Any relevant request or approval IDs (you can find these in the URL or on the detail page).',
              },
              {
                id: 'bullet-repro',
                text: 'Steps to reproduce the issue, if something went wrong.',
              },
              {
                id: 'bullet-screenshots',
                text: 'Screenshots or error messages - anything that helps the team see what you see.',
              },
            ],
          },
        ],
      },
    ],
    faqs: [
      {
        id: 'response-time',
        question: 'How long does it take to get a response?',
        answer:
          "Our support team typically responds within 24 hours. They'll reach out to you via email, so keep an eye on your inbox.",
      },
      {
        id: 'track-support-request',
        question: 'What happens after I submit?',
        answer:
          "You don't need to do anything else - our support team will reach out to you via email and work with you until your issue is resolved. If you have additional context to share in the meantime, you can always submit another message.",
      },
    ],
  },

  [FAQ_ARTICLE_IDS.PRODUCT_NOT_IN_CATALOG]: {
    title: "What if my product isn't listed?",
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `The product database contains thousands of AI tools from across the market. When you [[start a request|${FAQ_ARTICLE_IDS.HOW_TO_SUBMIT}]], the **Find** step lets you search this database by product or company name.\nIf the tool you need isn't listed yet, that's completely fine - you can add it yourself using a **custom entry**. Your request goes through the same review process, and the product gets added to the database for everyone to find in the future.`,
      },
      {
        id: 'steps-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'How to add and request a product',
        icon: 'package-search',
        color: 'brand',
        blocks: [
          {
            id: 'steps',
            type: CONTENT_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'step-start',
                title: 'Start a new request',
                text: 'Click {btn:request-approval} to begin.',
                jumpTo: {
                  name: ROUTES.TICKET_EDIT,
                  params: { ticketStep: TICKET_EDIT_STEPS.PRODUCT },
                },
              },
              {
                id: 'step-custom',
                title: 'Switch to custom entry',
                text: "In the **Find** step, search for the product. If it doesn't come up, click {btn:didnt-find-what-youre-looking-for} or switch to the **Custom Entry** tab.",
              },
              {
                id: 'step-enter',
                title: 'Enter the product details',
                text: 'Provide the **Company name** and **Product name**. Also add the **Company website**, and the **Product website** when the product is not already in the catalog.\nWebsites are optional, but a correct address helps your administrator identify the tool and research it. If the company is already listed, its website is filled in for you.\nUse accurate, complete names - this information is used to research the product and add it to the database.',
              },
              {
                id: 'step-continue',
                title: 'Complete and submit',
                text: 'Click {btn:create-request}, fill out the questionnaire in the **Explain** step, and {btn:submit-request} when ready. From here, the process is the same as for any other product.',
              },
            ],
          },
        ],
      },
      {
        id: 'what-happens-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'What happens with your custom entry',
        icon: 'sparkles',
        color: 'info',
        blocks: [
          {
            id: 'what-happens-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `When you submit a custom entry, the platform researches the product and adds it to the database. That means the next person looking for the same tool will find it right away.\nYour request is reviewed like any other - your administrator evaluates it and makes a decision. If you have questions before submitting, you can always [[contact support|${FAQ_ARTICLE_IDS.CONTACTING_SUPPORT}]].`,
          },
        ],
      },
      {
        id: 'Note - policy',
        type: CONTENT_BLOCK_TYPES.NOTE,
        icon: 'alert-circle',
        label: 'Important',
        text: "Using AI tools outside the platform's governance process may violate your organization's policies.\nAlways request access through the platform, even for tools not yet in the database.",
      },
    ],
    faqs: [
      {
        id: 'non-catalog-review',
        question: 'Is a custom entry request reviewed differently?',
        answer:
          'No - it goes through the exact same review process as any other request. Your administrator reviews it and makes a decision just like they would for a product already in the database.',
      },
      {
        id: 'custom-entry-database',
        question: 'Does my custom entry get added to the database?',
        answer:
          "Yes. When you submit a custom entry, the platform researches the product and adds it to the database. Future users searching for the same tool will find it listed - so you're helping the database grow for everyone.",
      },
      {
        id: 'custom-entry-website',
        question: 'Should I include a website?',
        answer:
          'Yes, if you have one. **Company website** and **Product website** are optional, but a correct website makes it much easier to identify the tool and add it to the database. If the company is already listed, its website is filled in for you.',
      },
    ],
  },

  [FAQ_ARTICLE_IDS.INTEGRATIONS]: {
    title: 'Integrations',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: 'Integrations connect the platform to the tools you already use, so important updates come to you instead of the other way around.\nRather than checking back for status changes, you get notified right where you work.',
      },
      {
        id: 'what-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'What integrations do',
        icon: 'workflow',
        color: 'brand',
        blocks: [
          {
            id: 'what-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `When an integration is active, the platform sends you personal notifications through that tool. That includes decisions on your [[requests|${FAQ_ARTICLE_IDS.WHAT_IS_A_REQUEST}]] (approved or denied), messages from administrators reviewing your requests, and changes to your [[approvals|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]].\nYou control which integrations are connected from **Settings**.`,
          },
        ],
      },
      {
        id: 'slack-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Slack',
        icon: 'message-square',
        color: 'info',
        blocks: [
          {
            id: 'slack-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `Connecting your Slack account lets you receive platform notifications directly in Slack - no need to keep the platform open to stay informed.\nIf your Slack account uses a different address than the one you sign in with, add and verify that address under **User details** - see [[additional email addresses|${FAQ_ARTICLE_IDS.ADDITIONAL_EMAIL_ADDRESSES}]].`,
          },
          {
            id: 'slack-benefits',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            title: 'What you get',
            items: [
              {
                id: 'benefit-decisions',
                text: 'Notified when your request is approved or denied.',
              },
              {
                id: 'benefit-workspace',
                text: 'Stay updated without leaving your Slack workspace.',
              },
              {
                id: 'benefit-preferences',
                text: `[[Choose which updates reach you|${FAQ_ARTICLE_IDS.SET_UP_NOTIFICATIONS}]] in Slack from **Settings** → **Notifications**.`,
              },
            ],
          },
          {
            id: 'slack-steps',
            type: CONTENT_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'step-settings',
                title: 'Go to Settings',
                text: 'Open **Settings** from the left menu, then switch to the **Integrations** tab.',
                jumpTo: { name: ROUTES.SETTINGS_INTEGRATIONS },
              },
              {
                id: 'step-connect',
                title: 'Connect your Slack',
                text: 'Click **Connect my Slack** and follow the steps to link your Slack account.',
              },
              {
                id: 'step-done',
                title: "You're set",
                text: 'Once connected, notifications start arriving in Slack automatically. You can send a test message from the same page to make sure everything is working.',
              },
            ],
          },
          {
            id: 'slack-org-note',
            type: CONTENT_BLOCK_TYPES.NOTE,
            icon: 'info',
            label: 'Tip',
            text: "If Slack hasn't been enabled for your organization yet, you'll see a message letting you know. Contact your administrator to get it set up.",
          },
        ],
      },
      {
        id: 'managing-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Managing your connection',
        icon: 'settings',
        color: 'positive',
        blocks: [
          {
            id: 'managing-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `You can disconnect from Slack anytime from the **Integrations** tab in **Settings**. While connected, click **Notification settings** on the Slack card, or open the **Notifications** tab, to [[choose which updates reach you|${FAQ_ARTICLE_IDS.SET_UP_NOTIFICATIONS}]] in **Slack Notifications**.\nYou can also send a test message to verify your connection is working.\nIf you disconnect and change your mind later, just reconnect from the same page - your platform data is not affected, and your Slack choices come back as you left them.`,
          },
        ],
      },
      {
        id: 'Note - disconnect',
        type: CONTENT_BLOCK_TYPES.NOTE,
        icon: 'alert-circle',
        label: 'Important',
        text: `Disconnecting from Slack only stops notifications. Your [[requests|${FAQ_ARTICLE_IDS.WHAT_IS_A_REQUEST}]], [[approvals|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]], and [[confirmations|${FAQ_ARTICLE_IDS.WHAT_ARE_CONFIRMATIONS}]] are not affected.`,
      },
    ],
    faqs: [
      {
        id: 'slack-notification-types',
        question: 'What notifications will I get in Slack?',
        answer: `By default, Slack tells you when your [[request|${FAQ_ARTICLE_IDS.WHAT_IS_A_REQUEST}]] is approved or denied, when an administrator messages you about a request, and when one of your [[approvals|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]] is revoked, reinstated, or has its conditions changed.\nAfter you connect Slack, open **Settings** → **Notifications** and use **Slack Notifications** to turn each type on or off (or choose **Allow all notifications**), then click **Save changes** - see [[set up notifications|${FAQ_ARTICLE_IDS.SET_UP_NOTIFICATIONS}]]. If nothing arrives, see [[notifications not arriving|${FAQ_ARTICLE_IDS.NOTIFICATIONS_NOT_ARRIVING}]].\nAll notifications are personal - only you see them in your Slack.`,
      },
      {
        id: 'slack-org-availability',
        question: 'Does everyone in my organization have Slack integration?',
        answer: `It depends on your organization's setup. If Slack hasn't been enabled yet, you'll see a notice on the Integrations page letting you know. Contact your administrator or [[reach out to support|${FAQ_ARTICLE_IDS.CONTACTING_SUPPORT}]] to request it.`,
      },
      {
        id: 'slack-reconnect',
        question: 'Can I reconnect after disconnecting?',
        answer:
          'Yes, anytime. Go to **Settings**, open the **Integrations** tab, and click **Connect my Slack** again. Your previous connection details are cleared, so you go through the setup fresh - but your Slack notification choices come back exactly as you left them.',
      },
    ],
  },

  [FAQ_ARTICLE_IDS.ADDITIONAL_EMAIL_ADDRESSES]: {
    title: 'Additional email addresses',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `Your sign-in email is how you log in to the platform. **Additional email addresses** are separate verified addresses you can add when your organization allows it.\nThey matter when an integration is tied to a different inbox - today that is mainly [[Slack|${FAQ_ARTICLE_IDS.INTEGRATIONS}]] if your Slack account does not use the same address you sign in with.`,
      },
      {
        id: 'why-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Why add another address?',
        icon: 'mail',
        color: 'brand',
        blocks: [
          {
            id: 'why-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: 'Integrations recognize you by email. If the address on your Slack workspace (or another connected tool) is not your sign-in email, verifying that address on your profile lets the platform match your account correctly.\nYour **primary** sign-in email stays the same - additional addresses do not replace it.',
          },
          {
            id: 'why-bullets',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            title: 'What you can do',
            items: [
              {
                id: 'why-verify',
                text: 'Add an address, confirm it with a **6-digit verification code** sent to that inbox, and use it for eligible integrations.',
              },
              {
                id: 'why-remove',
                text: 'Remove a verified additional email anytime you no longer need it.',
              },
              {
                id: 'why-pending',
                text: 'See **Pending** addresses while a code is outstanding; codes expire if you do not verify in time.',
              },
            ],
          },
        ],
      },
      {
        id: 'how-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Add and verify an address',
        icon: 'badge-check',
        color: 'cta',
        blocks: [
          {
            id: 'how-steps',
            type: CONTENT_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'step-settings',
                title: 'Open User details',
                text: 'Go to **Settings** and stay on the **User details** tab. Scroll to **Email addresses**.',
                jumpTo: { name: ROUTES.SETTINGS_DETAILS },
              },
              {
                id: 'step-add',
                title: 'Add the address',
                text: 'Click **+ Add email address**, enter the email you want to verify, then click **Send code**.',
              },
              {
                id: 'step-inbox',
                title: 'Check your inbox',
                text: 'A **6-digit verification code** is emailed to that address. Use the most recent code if you requested more than one.',
              },
              {
                id: 'step-verify',
                title: 'Enter the code',
                text: 'Click **Enter code** next to the pending address (or complete the verify step in the dialog), type the code, and click **Verify**. The address shows as **Verified** when it is ready to use.',
              },
            ],
          },
          {
            id: 'how-resend-note',
            type: CONTENT_BLOCK_TYPES.NOTE,
            icon: 'info',
            label: 'Tip',
            text: 'If a code expires, use **Resend** or **Resend code** to get a new one. Pending rows show when the current code expires.',
          },
        ],
      },
      {
        id: 'notifications-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Portal email vs integrations',
        icon: 'bell',
        color: 'info',
        blocks: [
          {
            id: 'notifications-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: 'Today, platform email notifications (for example messages about your requests) still go to your **sign-in email**. Additional addresses are for matching integrations such as Slack, not for choosing where portal email is delivered yet.\nChoosing a preferred address to **receive emails** from the platform is planned - there is no fixed date for that yet.',
          },
        ],
      },
      {
        id: 'visibility-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'If you do not see Email addresses',
        icon: 'building',
        color: 'violet',
        blocks: [
          {
            id: 'visibility-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: "The **Email addresses** section appears only when your organization has turned on additional emails for this portal. You cannot enable it yourself - your organization's administrator controls that setting in the management portal.\nIf the section is missing, contact your administrator if you need to verify an address for Slack or another integration.",
          },
        ],
      },
    ],
    faqs: [
      {
        id: 'additional-change-sign-in',
        question: 'Does adding an email change how I sign in?',
        answer:
          'No. You still sign in with your primary email. Additional addresses are verified extras on your profile only.',
      },
      {
        id: 'additional-receive-emails',
        question: 'Can I choose which address receives platform emails?',
        answer:
          'Not yet. All portal email goes to your sign-in address today. Additional emails help with integrations (such as Slack). A way to pick which address receives notifications is planned.',
      },
      {
        id: 'additional-missing-section',
        question: 'Why is there no Email addresses section in Settings?',
        answer:
          'Your organization has additional emails turned off for this portal, or the feature is not enabled for you yet. Only an organization administrator can turn it on from the management portal.',
      },
      {
        id: 'additional-search-slack',
        question: 'How does this relate to Slack?',
        answer: `If Slack uses a different email than your sign-in address, verify that address here, then connect Slack from [[Integrations|${FAQ_ARTICLE_IDS.INTEGRATIONS}]].`,
      },
    ],
  },

  [FAQ_ARTICLE_IDS.SET_UP_NOTIFICATIONS]: {
    title: 'Set up notifications',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `Notifications tell you when something changes on your requests or approvals, so you don't have to keep checking.\nYou choose which updates reach you by email and in Slack from **Settings** → **Notifications**. If something you expect never arrives, see [[notifications not arriving|${FAQ_ARTICLE_IDS.NOTIFICATIONS_NOT_ARRIVING}]].`,
      },
      {
        id: 'choose-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Choose which alerts reach you',
        icon: 'list-checks',
        color: 'brand',
        blocks: [
          {
            id: 'choose-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: "The **Notifications** tab has two cards: **Email Notifications** and **Slack Notifications**. Each card lists the same six types, and every type starts on for both - so you only switch off what you don't need.\nEmail goes to your **sign-in email**.",
          },
          {
            id: 'choose-types',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            title: 'Notification types',
            items: [
              {
                id: 'type-approved',
                text: `**Request approved** - an administrator approves your [[request|${FAQ_ARTICLE_IDS.WHAT_IS_A_REQUEST}]].`,
              },
              {
                id: 'type-denied',
                text: '**Request denied** - an administrator denies your request.',
              },
              {
                id: 'type-message',
                text: '**New message on your request** - an administrator sends you a message on one of your requests.',
              },
              {
                id: 'type-revoked',
                text: `**Approval revoked** - an administrator revokes one of your active [[approvals|${FAQ_ARTICLE_IDS.WHAT_IS_AN_APPROVAL}]].`,
              },
              {
                id: 'type-reinstated',
                text: '**Approval reinstated** - an administrator reinstates a revoked approval.',
              },
              {
                id: 'type-conditions',
                text: '**Approval conditions changed** - an administrator changes the conditions of one of your active approvals.',
              },
            ],
          },
          {
            id: 'choose-steps',
            type: CONTENT_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'step-open',
                title: 'Open Notifications',
                text: 'Open **Settings** from the left menu, then switch to the **Notifications** tab.',
                jumpTo: { name: ROUTES.SETTINGS_NOTIFICATIONS },
              },
              {
                id: 'step-choose',
                title: 'Pick your types',
                text: 'In a card, turn each type on or off, or turn on **Allow all notifications** for everything. If a card is closed, click its header to open it.',
              },
              {
                id: 'step-save',
                title: 'Save the card',
                text: 'Click **Save changes** on that card. Each card saves on its own, and its badge shows **Unsaved changes** until you do. **Discard** puts back what you last saved.',
              },
            ],
          },
        ],
      },
      {
        id: 'slack-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Slack needs your connection',
        icon: 'message-square',
        color: 'info',
        blocks: [
          {
            id: 'slack-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `The **Slack Notifications** card works only after you connect your Slack account on the **Integrations** tab - see [[integrations|${FAQ_ARTICLE_IDS.INTEGRATIONS}]]. Until then, the card shows **Not connected**. If Slack hasn't been enabled for your organization, the card tells you so - ask your administrator to set it up.\nOnce connected, **Notification settings** on the Slack card in **Integrations** takes you straight to this tab.`,
          },
          {
            id: 'slack-reconnect-note',
            type: CONTENT_BLOCK_TYPES.NOTE,
            icon: 'info',
            label: 'Tip',
            text: 'Your Slack choices are kept while you are disconnected. When you reconnect, Slack sends exactly the types you chose before.',
          },
        ],
      },
      {
        id: 'in-app-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Updates inside the portal',
        icon: 'bell-ring',
        color: 'positive',
        blocks: [
          {
            id: 'in-app-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: "Updates that appear while the portal is open have no setting on this tab. They need your browser to allow notifications for this site - if it doesn't, a banner tells you how to fix it.",
          },
        ],
      },
    ],
    faqs: [
      {
        id: 'set-up-turn-off',
        question: 'Can I turn off every notification?',
        answer:
          'Yes. Turn off every type in a card and click **Save changes** - the card then shows **Disabled**. Your requests and approvals still update in the portal.',
      },
      {
        id: 'set-up-confirmations',
        question: 'Why is there no type for confirmations?',
        answer: `The six types cover decisions, messages, and changes to your approvals. Due [[confirmations|${FAQ_ARTICLE_IDS.WHAT_ARE_CONFIRMATIONS}]] show up in **Product confirmations** and on your [[home dashboard|${FAQ_ARTICLE_IDS.YOUR_HOME_DASHBOARD}]].`,
      },
    ],
  },

  [FAQ_ARTICLE_IDS.NOTIFICATIONS_NOT_ARRIVING]: {
    title: 'Notifications not arriving',
    content: [
      {
        id: 'intro',
        type: CONTENT_BLOCK_TYPES.PARAGRAPH,
        text: `A missing notification usually has a cause you can fix in a minute. Start with the channel that went quiet.\nTo choose which updates reach you, see [[set up notifications|${FAQ_ARTICLE_IDS.SET_UP_NOTIFICATIONS}]].`,
      },
      {
        id: 'checks-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Check your choices first',
        icon: 'list-checks',
        color: 'brand',
        blocks: [
          {
            id: 'checks-list',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            items: [
              {
                id: 'check-type-on',
                text: '**The type is on for that channel.** Open **Settings** → **Notifications** and check its toggle in **Email Notifications** or **Slack Notifications**.',
              },
              {
                id: 'check-saved',
                text: '**The card is saved.** A card that shows **Unsaved changes** has not been saved yet - click **Save changes** on that card.',
              },
            ],
          },
        ],
      },
      {
        id: 'slack-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Slack is silent',
        icon: 'message-square',
        color: 'info',
        blocks: [
          {
            id: 'slack-list',
            type: CONTENT_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            items: [
              {
                id: 'check-slack-connected',
                text: `**Your Slack is connected.** If the **Slack Notifications** card shows **Not connected**, connect your Slack on the **Integrations** tab - see [[integrations|${FAQ_ARTICLE_IDS.INTEGRATIONS}]].`,
              },
              {
                id: 'check-slack-org',
                text: "**Your organization has Slack.** If the card says Slack hasn't been enabled for your organization, only your administrator can turn it on.",
              },
              {
                id: 'check-slack-email',
                text: `**Slack can match your email.** If your Slack account uses a different address than your sign-in email, verify that address - see [[additional email addresses|${FAQ_ARTICLE_IDS.ADDITIONAL_EMAIL_ADDRESSES}]].`,
              },
            ],
          },
        ],
      },
      {
        id: 'email-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'An email never arrives',
        icon: 'mail',
        color: 'cta',
        blocks: [
          {
            id: 'email-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: 'Email notifications go to your **sign-in email**. Check your spam or junk folder, and any filters or rules that move mail out of your inbox.',
          },
        ],
      },
      {
        id: 'in-app-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Updates do not appear in the portal',
        icon: 'bell-off',
        color: 'cyan',
        blocks: [
          {
            id: 'in-app-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: 'Live updates inside the portal need your browser to allow notifications for this site. If it does not, a banner such as **Notifications blocked in browser** tells you what to change - follow its steps, then refresh the page.',
          },
        ],
      },
      {
        id: 'still-missing-section',
        type: CONTENT_BLOCK_TYPES.SECTION,
        title: 'Still nothing?',
        icon: 'help-circle',
        color: 'violet',
        blocks: [
          {
            id: 'still-missing-paragraph',
            type: CONTENT_BLOCK_TYPES.PARAGRAPH,
            text: `If every check passes and notifications still don't arrive, contact your administrator - they can check your organization's setup. You can also [[reach out to support|${FAQ_ARTICLE_IDS.CONTACTING_SUPPORT}]].`,
          },
        ],
      },
    ],
    faqs: [
      {
        id: 'not-arriving-request-changed',
        question: 'Does a missing notification mean nothing changed?',
        answer:
          'No. Your requests and approvals update in the portal either way. Check **Product requests** or **Product approvals** for the latest.',
      },
    ],
  },
}
